import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * The top-level application shell. The server emits this element's structure as Declarative Shadow
 * DOM for an instant first paint; on load, Lit adopts that same shadow root (ReactiveElement reuses
 * an existing `shadowRoot`) and takes over. Page content and the nav are projected through slots, so
 * the server-rendered light-DOM content is preserved across hydration.
 *
 * Navigation in the hybrid model is full-page (each route is server-rendered), so the shell simply
 * reflects the active `route` for styling; it does not perform client-side view swapping.
 */
@customElement('gc-app-shell')
export class GcAppShell extends LitElement {
  static override styles = css`
    :host { display: block; }
    .shell-header {
      display: flex; align-items: center; justify-content: space-between;
      gap: 1rem; color: #fff;
      /* Keep the brand/nav clear of the notch & rounded corners when the standalone PWA paints under
         the status bar (apple-mobile-web-app-status-bar-style=black-translucent). The dark header
         background still bleeds up under the notch because the inset rides in the padding. */
      padding: calc(0.85rem + env(safe-area-inset-top, 0px)) calc(1.25rem + env(safe-area-inset-right, 0px))
        0.85rem calc(1.25rem + env(safe-area-inset-left, 0px));
      background: linear-gradient(135deg, #0d2818 0%, #14512a 100%);
      box-shadow: 0 2px 14px rgba(13, 40, 24, 0.28);
    }
    .brand { display: inline-flex; align-items: center; gap: 0.6rem; color: #fff; text-decoration: none; font-weight: 800; font-size: clamp(1.15rem, 0.95rem + 0.9vw, 1.6rem); letter-spacing: -0.01em; }
    .brand-logo { width: clamp(2.5rem, 1.6rem + 4vw, 4rem); height: clamp(2.5rem, 1.6rem + 4vw, 4rem); border-radius: 50%; flex: none; }
    .shell-nav ::slotted(span) { display: flex; gap: 0.35rem; align-items: center; flex-wrap: wrap; }
    .shell-main { display: block; }
    /* Full-bleed pages (the game table) fill the viewport and hide the site header — the in-felt
       "Back to the lobby" link handles navigation. The felt itself (and the iOS-Safari under-toolbar
       fill) is owned by the root <html>/<body> in PageRenderer, which over-sizes them by
       --gc-safari-felt-overshoot to reach past Safari's reported bottom. */
    :host([full-bleed]) { height: 100dvh; display: flex; flex-direction: column; }
    :host([full-bleed]) .shell-header { display: none; }
    :host([full-bleed]) .shell-main { flex: 1 1 auto; min-height: 0; }
  `;

  /** The active client route (mirrors the server-provided value). */
  @property({ type: String, reflect: true })
  route = 'home';

  /** When set, the page content fills the viewport below the header (used by the game table). */
  @property({ type: Boolean, reflect: true, attribute: 'full-bleed' })
  fullBleed = false;

  override connectedCallback(): void {
    // The server emits this element's structure as Declarative Shadow DOM for instant first paint.
    // Without Lit hydration markers, lit-html would render a *second* copy after the server's markup
    // (duplicate header/main). Discard the server shadow content once so Lit renders a single
    // authoritative copy; the markup is identical and the static styles re-apply via adoptedStyleSheets.
    if (!this.hasUpdated && this.shadowRoot) {
      this.shadowRoot.replaceChildren();
    }
    super.connectedCallback();
  }

  /** The authenticated user's display name, if any (set by the server on the shell element). */
  @property({ type: String, attribute: 'display-name' })
  displayName = '';

  override render() {
    return html`
      <header part="header" class="shell-header">
        <a class="brand" href="/"><img class="brand-logo" src="/images/gnome.svg" alt="" width="28" height="28" />Gnome Cards</a>
        <nav class="shell-nav" aria-label="Primary">
          <slot name="nav"></slot>
        </nav>
      </header>
      <main part="main" class="shell-main">
        <slot></slot>
      </main>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-app-shell': GcAppShell;
  }
}

